import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo, useState } from "react";

import { createHead } from "@/components/createHead";
import { CustomNodeSearchResult } from "@/components/CustomNodeSearchResult";
import { LoadMoreSpinner } from "@/components/LoadMoreSpinner";
import {
  MarketplaceBody,
  MarketplaceTitle,
} from "@/components/MarketplacePage";
import { SearchInput } from "@/components/ui/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import { searchCustomNodesQueryOptions } from "@/lib/api";

const PAGE_SIZE = 25;

export const Route = createFileRoute("/_marketplace-layout/custom-nodes/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      q: search.q || "",
    } as { q?: string };
  },
  loader: async ({ context: { queryClient }, location }) => {
    const q = (location.search as { q: string }).q;
    await queryClient.prefetchInfiniteQuery(
      searchCustomNodesQueryOptions({
        query: q,
        page: 1,
        page_size: PAGE_SIZE,
      }),
    );
  },
  head: () => {
    return createHead({
      title: "Custom Nodes | Graze",
      ogTitle: "Custom Nodes",
      description:
        "Use custom bits of logic from the community to supercharge your feeds. Custom nodes are created by other users to make creating great feeds easier.",
      pathname: "/custom-nodes",
      ogImage: "/custom-nodes.png",
    });
  },
});

function Results({ q }: { q: string }) {
  const { data, fetchNextPage, isLoading } = useInfiniteQuery(
    searchCustomNodesQueryOptions({
      query: q,
      page: 1,
      page_size: PAGE_SIZE,
    }),
  );

  const allResults = useMemo(() => {
    return data?.pages.flatMap((d) => d.results) || [];
  }, [data]);

  let items = null;

  if (allResults.length > 0) {
    items = allResults.map((node) => (
      <Link
        key={node.id}
        to="/custom-nodes/$nodeId"
        params={{ nodeId: node.id.toString() }}
        className="flex"
      >
        <CustomNodeSearchResult item={node} />
      </Link>
    ));
  } else {
    items = Array.from({ length: 12 }).map((_, index) => {
      return (
        <Skeleton
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={"skeleton-" + index}
          className="group p-4 border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden"
          style={{ height: 180 }}
        />
      );
    });
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items}
      </div>

      {data &&
        data.pages[data.pages.length - 1]?.results.length === PAGE_SIZE && (
          <LoadMoreSpinner
            fetchNextPage={fetchNextPage}
            isLoading={isLoading}
          />
        )}
    </>
  );
}

function RouteComponent() {
  const { q = "" } = Route.useSearch();
  const [search, setSearch] = useState(q);
  const activeSearch = useDebounce(search, 500);

  return (
    <>
      <MarketplaceTitle>Custom Nodes</MarketplaceTitle>
      <MarketplaceBody>
        <div className="flex flex-col gap-2 max-w-[600px] w-full mx-auto mb-10">
          <SearchInput
            value={search}
            onChange={setSearch}
            aria-label="Search custom nodes"
            className="border-2 border-teal-7 dark:border-tealdark-7 bg-teal-6 dark:bg-tealdark-6 text-teal-normal text-lg! h-auto"
          />
        </div>

        <Results q={activeSearch} />
      </MarketplaceBody>
    </>
  );
}
